import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, signal, WritableSignal, ModelSignal, model, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Observable } from 'rxjs';
import { Incident, IncidentReportStatuses, IncidentsService, REPORT_COMPLETE_STEP, ViewIncident } from '../../../api/incidents.service';
import { LinkableIncidentService } from '../../../api/linkable-incident.service';
import { Workflow, WorkflowStep } from '../../../api/workflow.service';
import { LocalAuthService } from '../../../auth/local-auth.service';
import { OnAssignValues } from '../assign-incident/assign-incident.component';
import { IncidentAction } from '../incident-table2/incident-table2.component';
import { OnLinkValues } from '../link-report/link-report.component';
import { IncidentModalService } from '../modals/incident-modal.service';
import { MessageBoxResults } from '../modals/message-box/message-box.component';

enum IncidentListType {
  OpenIncidents = 0,
  AllIncidents = 1,
  MyIncidents = 2,
}

interface IncidentList {
  name: string,
  type: IncidentListType,
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    providers: [DialogService],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HomeComponent implements OnInit, AfterViewInit {


  unlinkedReportActions: IncidentAction[] = [
    { label: "Link", size: "sm", style: "primary", action: (incident: Incident) => this.linkIncidentReport(incident) },
    { label: "Delete", size: "sm", style: "primary", validSteps: [IncidentReportStatuses.Requested], action: (incident: Incident) => this.deleteIncidentReport(incident) }
  ]

  openIncidentActions: IncidentAction[] = [
    { label: "Assign", size: "sm", style: "primary", action: (incident: Incident) => this.assignIncident(incident) },
    { label: "Link Trip", size: "sm", style: "primary", action: (incident: Incident) => this.linkTrip(incident) },
  ]

  myReportActions: IncidentAction[] = [
    { label: "Get New Link", size: "sm", style: "primary", action: (incident: Incident) => this.getRequestLink(incident) },
  ]

  createMenuItems: WritableSignal<MenuItem[]> = signal([]);
  linkableIncidents: WritableSignal<Incident[]> = signal([]);
  unlinkedReports: WritableSignal<ViewIncident[]> = signal([]);
  incidents: WritableSignal<ViewIncident[]> = signal([]);
  myIncidentReports: WritableSignal<ViewIncident[]> = signal([]);
  workflows: WritableSignal<Workflow[]> = signal([]);
  workflowSteps: WritableSignal<WorkflowStep[]> = signal([]);


  incidentLists: IncidentList[] = [
    {
      name: "Active Incidents",
      type: IncidentListType.OpenIncidents,
    },
    {
      name: "All Incidents",
      type: IncidentListType.AllIncidents,
    },
    {
      name: "My Open Incidents",
      type: IncidentListType.MyIncidents,
    }
  ]

  selectedIncidentList: ModelSignal<IncidentList> = model(this.incidentLists[0]);
  showSeverityImpact = computed(() => this.selectedIncidentList().type === IncidentListType.AllIncidents);
  incidentsLoading: WritableSignal<boolean> = signal(false);
  linkReportsLoading: WritableSignal<boolean> = signal(false);
  myReportsLoading: WritableSignal<boolean> = signal(false);

  constructor(
    private incidentService: IncidentsService,
    private incidentModalService: IncidentModalService,
    private dialogService: DialogService,
    private router: Router,
    public authService: LocalAuthService,
    private linkableIncidentService: LinkableIncidentService
  ) { }

  ngAfterViewInit(): void {

  }


  ngOnInit(): void {

    this.loadUnattachedIncidentReports();
    this.loadIncidents();
    this.buildMenuItems();

    if (this.authService.hasRole("crisis"))
      this.linkableIncidentService.getIncidents()
        .subscribe(incidents => {
          this.linkableIncidents.set(incidents);
        })


    this.myReportsLoading.set( true);
    this.incidentService.getMyIncidentReports().subscribe({
      next: reports => {
        this.myIncidentReports.set(reports);
        this.myReportsLoading.set(false);
      },
      error: err => {
        console.error(err);
        this.myReportsLoading.set( false);
      }
    })

  }

  buildMenuItems() {

    const menuItems: MenuItem[] = []
    if (this.authService.hasRole("crisis") ) {
      menuItems.push({label: "Incident", command: () => this.router.navigate(['incidents','create'])})
    }

    menuItems.push({label: "Staff or Participant Report", command: () => this.router.navigate(['incidents','incident-reports', 'staff-or-participant', 'create'])})
    menuItems.push({label: "Supervisor Report", command: () => this.router.navigate(['incidents','incident-reports', 'supervisor', 'create'])})
    menuItems.push({label: "Witness Report", command: () => this.router.navigate(['incidents', 'incident-reports', 'witness', 'create'])})
    menuItems.push({label: "Missing Person Report", command: () => this.router.navigate(['incidents','incident-reports','missing-person','create'])})
    this.createMenuItems.set(menuItems)
  }

  linkIncidentReport(incident: Incident) {

    this.incidentModalService.showLinkReportModal(this.dialogService, this.linkableIncidents(), incident.workItemId).onClose.subscribe(
      (result: OnLinkValues | undefined) => {
        if (result == OnLinkValues.LinkSuccess) {
          this.loadUnattachedIncidentReports();
        }

        if (result == OnLinkValues.CreateIncident) {
          this.router.navigate(['incidents', 'create'], { state: { incidentReport: incident } })
        }
      }
    )
  }

  deleteIncidentReport(incident: Incident) {
    this.incidentModalService.showMessageBox(this.dialogService, "Delete Incident Report Request?", "This will delete the requested incident report. This cannot be undone. Are you sure?", {
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    }).onClose.subscribe(
      (result: MessageBoxResults) => {
        if (result == MessageBoxResults.Confirmed) {
          this.incidentService.deleteIncidentReport(incident.workItemId).subscribe(
            () => this.loadUnattachedIncidentReports()
          )
        }
      }
    )
  }

  assignIncident(incident: Incident) {
    this.incidentModalService.showAssignModal(this.dialogService, incident.workItemId).onClose.subscribe(
      (result: OnAssignValues | undefined) => {
        if (result === OnAssignValues.AssignSuccess) {
          this.loadIncidents()
        }
      }
    )
  }

  linkTrip(incident: Incident) {

    this.incidentModalService.showTripLinkModal(this.dialogService, incident.workItemId).onClose.subscribe(
      (result: OnLinkValues | undefined) => {
        if (result == OnLinkValues.LinkSuccess) {
          this.loadIncidents()
        }
      }
    )
  }



  loadUnattachedIncidentReports() {
    if (this.authService.hasRole("crisis")) {
      this.linkReportsLoading.set(true);
      this.incidentService.getUnattachedIncidentReports()
        .subscribe({
          next: results => {
            this.unlinkedReports.set(results);
            this.linkReportsLoading.set(false);
          },
          error: err => {
            console.error(err);
            this.linkReportsLoading.set(false);
          }
        })
    }
  }

  loadIncidents() {
    if (this.authService.hasRole("crisis")) {
      this.incidentsLoading.set(true);
      let incidents$: Observable<ViewIncident[]>
      switch (this.selectedIncidentList().type) {
        case IncidentListType.OpenIncidents:
          incidents$ = this.incidentService.getOpenIncidents();
          break;
        case IncidentListType.AllIncidents:
          incidents$ = this.incidentService.getIncidents();
          break;
        case IncidentListType.MyIncidents:
          incidents$ = this.incidentService.getMyAssignedIncidents();
          break;
      }


      incidents$
        .subscribe({
          next: results => {
            this.incidents.set( results);
            this.incidentsLoading.set(false)
          },
          error: err => {
            console.error(err);
            this.incidentsLoading.set(false)
          }
        })
    }
  }

  onOpenIncidentClick(incident: Incident) {
    this.router.navigate(['incidents', incident.workItemId])
  }

  onUnlinkedReportClick(incident: Incident) {
    this.router.navigate(['incident-reports', incident.workItemId])
  }

  getRequestLink(incident: Incident) {

    if (incident.currentStepId === REPORT_COMPLETE_STEP) {
      this.incidentModalService.showMessageBox(
        this.dialogService ,
        "Report Aready Complete", 
        "This report has alredy been completed")
    }
    else {
      this.incidentService.getExistingReportLink(incident.workItemId).subscribe({
        next: result => {
          this.incidentModalService.showMessageBox(
            this.dialogService,
            "Report Request Link", 
            result.link, 
            { showCopyButton: true })


        }
      })
    }


  }

}
